import React, { useContext, useCallback } from 'react';
import { useRouter } from 'next/router';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Link from 'next/link';
import Box from "@mui/material/Box";
import { EmailShareButton, EmailIcon } from "react-share";
import CurrentPageContext from '@/components/context/CurrentPageData';
import Modal from '@mui/material/Modal';
import { bool } from 'prop-types';

const useStyles = makeStyles((theme) =>
  createStyles({
    iconLink: {
      margin: '0 0 1rem',
      color: theme.palette.primary.main,
      "& a": {
        color: theme.palette.primary.main,
        fontFamily: theme.typography.fontFamily,
        textDecoration: 'none',
        fontSize: 20,
        margin: '0 .75rem',
      },
      "& svg": {
        width: 25,
        height: 25,
        marginBottom: '-4px',
      },
    },
    fixIcon: {
      '& svg': {
        transform: 'translate(-3px, 2.5px)'
      }
    },
    root: {
      maxWidth: '100%',
      position: 'relative',
      color: theme.palette.grey[900],
      textAlign: 'center',
      '& svg': {
        fill: theme.palette?.tealBlue?.dark
      },
      "&.rental": {
        color: theme.palette?.tealBlue?.dark || '#006680',
      },
      "& a": {
        textDecoration: 'none',
      },
      "& span": {
        fontSize: 18,
        fontWeight: 400,
      },
      "& .print-button": {
        backgroundColor: 'transparent',
        border: 'none',
        "&:hover": {
          cursor: 'pointer',
        }
      },
      "& .waterfront": {
        backgroundColor: theme.palette.primary.light,
        color: 'black',
        display: 'block',
        margin: '1rem 0',
        width: '300px'
      },
    },
    floorplanLink: {
      "& a": {
        color: theme.palette.primary.main,
        fontSize: 18,
        fontWeight: 'bold',
        padding: '1rem 0 1rem 1rem',
      },
    },
    modalWindow: {
      maxWidth: 680,
      height: 'auto',
      maxHeight: '98vh',
      overflow: 'auto',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      top: '50%',
      padding: '1.75rem .75rem .75rem .75rem',
      position: 'fixed',
      backgroundColor: 'white',
      [theme.breakpoints.down('lg')]: {
        // top: 0,
        // left: 0,
        // width: '100vw',
        // height: '100vh',
        // transform: 'none',
      },
    },
    modalClose: {
      position: 'absolute',
      top: 4,
      right: '0',
      width: 40,
      height: 40,
    },

  }),
);

export default function ListingCTA(props) {
  const classes = useStyles();
  const router = useRouter();
  const sitewideContext = useContext(CurrentPageContext);
  const iFrameParameterBaywood = 'https://fs3.formsite.com/g4S24p/9vcgsi4ot7/fill?id8=' + router.asPath;
  const iFrameParameter = 'https://fs3.formsite.com/g4S24p/dcmm8afprx/fill?id8=' + router.asPath;

  const { node, ctaType } = props;
  let {
    address,
    beds,
    bathFull,
    bathHalf,
    bathAll,
    title,
    headline,
    homesite,
    price,
    leaseFee,
    houseFiles,
    sqft,
    url,
    internal,
    alias,
    virtualTour,
  } = node;

  if (node?.sfhouse?.id) {
    ({
      address,
      beds,
      bathFull,
      bathHalf,
      bathAll,
      houseFiles,
      homesite,
      sqft,
      virtualTour,
    } = node.sfhouse);
  }
  title = (ctaType === 'rental') ? (title || address) : (node.sfhouse?.address || node.sfhouse?.title || title);
  bathFull = bathFull || 0;
  bathHalf = bathHalf || 0;

  let splitBaths = 0;
  if (bathFull > 0 && bathHalf > 0) {
    splitBaths = 1;
  }
  bathAll = Math.round(bathAll * 100) / 100;
  bathAll = (bathAll && parseFloat(bathAll) > 0) ? parseFloat(bathAll) : (parseInt(bathFull) + parseInt(bathHalf));

  const currentURL = `${process.env.NEXT_PUBLIC_FRONT_URL}${router?.asPath}`;
  // getModalStyle is not a pure function, we roll the style only on the first render
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePrint = useCallback((e) => {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      window.print();
    }
  }, []);

  return (
    <Box align="center" my={4} className={`${classes.root}${ctaType === 'rental' ? ' rental' : ' sale'}`}>
      <Grid container spacing={3} justifyContent="center">
        {ctaType === 'rental' &&
          <>
            <Grid item xs={12} className="listingCta">
              <Box align="center">
                {title &&
                  <Typography variant="h1" gutterBottom>{title}</Typography>
                }
                {headline &&
                  <Typography component={`p`} gutterBottom>{headline}</Typography>
                }
                <Box display='flex' justifyContent='center' className={classes.iconBox}>
                  {beds > 0 &&
                    <Typography component="span">{beds} &nbsp;
                      <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.0475 7.08658H8.46507V12.8462H1.88266V5.44098H0.237061V17.783H1.88266V15.3146H16.6931V17.783H18.3387V10.3778C18.3387 9.5049 17.9919 8.66777 17.3747 8.05055C16.7575 7.43333 15.9204 7.08658 15.0475 7.08658ZM5.17386 12.0234C5.82853 12.0234 6.45637 11.7633 6.91929 11.3004C7.3822 10.8375 7.64227 10.2096 7.64227 9.55498C7.64227 8.90032 7.3822 8.27247 6.91929 7.80956C6.45637 7.34664 5.82853 7.08658 5.17386 7.08658C4.5192 7.08658 3.89136 7.34664 3.42844 7.80956C2.96553 8.27247 2.70546 8.90032 2.70546 9.55498C2.70546 10.2096 2.96553 10.8375 3.42844 11.3004C3.89136 11.7633 4.5192 12.0234 5.17386 12.0234Z" fill="inherit" />
                      </svg>&nbsp;BEDROOMS&nbsp; &nbsp; &nbsp;
                    </Typography>
                  }
                  {bathAll > 0 &&
                    <Typography component="span">{bathAll} &nbsp;
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 512 512">
                        <path d="M32,384c0,28.3,12.5,53.5,32,71.1V496c0,8.8,7.2,16,16,16h32c8.8,0,16-7.2,16-16v-15.1h256V496c0,8.8,7.2,16,16,16h32c8.8,0,16-7.2,16-16v-40.9c19.5-17.6,32-42.8,32-71.1v-32H32V384z M496,256H96V77.2c0-10.8,15-17,22.6-9.4l13.8,13.8c-8.8,26.9-3,52.8,11.8,71.5c-6.3,6.3-6.4,16.6-0.2,22.8l11.3,11.3c6.2,6.2,16.4,6.2,22.6,0L283.3,81.9c6.2-6.2,6.2-16.4,0-22.6L272,48c-6.2-6.2-16.4-6.2-22.6,0c-18.7-14.7-44.7-20.4-71.7-11.6l-13.8-13.8C149.5,8.2,129.6,0,109.3,0C66.7,0,32,34.7,32,77.2V256l-16,0c-8.8,0-16,7.2-16,16v32c0,8.8,7.2,16,16,16h480c8.8,0,16-7.2,16-16v-32C512,263.2,504.8,256,496,256z" fill="inherit" />
                      </svg>&nbsp;BATHROOMS&nbsp; &nbsp; &nbsp;
                    </Typography>
                  }
                  {node?.maxOcc > 0 &&
                    <Typography className={classes.fixIcon} component="span">
                      {node.maxOcc}&nbsp;&nbsp;&nbsp;
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" fill="currentColor" className="bi bi-people-fill" viewBox="0 0 16 16">
                        <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                        <path fillRule="evenodd" d="M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z" />
                        <path d="M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                      </svg>
                      OCCUPANCY</Typography>

                  }
                </Box>
                {node?.policies?.length > 0 &&
                  <>
                    <br />
                    <Box my={3}>
                      <Typography component="span">
                        POLICIES: {node.policies.join('  ')}
                      </Typography>
                    </Box>
                  </>
                }
              </Box>
            </Grid>
            {(houseFiles?.length > 0 || node?.rateCalFile?.length > 0) &&
              <Grid item xs={12}>
                {houseFiles?.length > 0 &&
                  houseFiles.map((v, i) => {
                    return (
                      <Button
                        key={`files-${i}`}
                        disableElevation
                        href={v.src}
                        variant="contained"
                        size="medium">DOWNLOAD PROPERTY FILE #{i + 1}</Button>
                    );
                  })
                }

                {node?.rateCalFile?.length > 0 &&
                  node.rateCalFile.map((v, i) => {
                    return (
                      <Button
                        key={`rate-files-${i}`}
                        disableElevation
                        href={v.src}
                        variant="contained"
                        size="medium">DOWNLOAD "{title}" CALENDAR</Button>
                    );
                  })
                }
                <br />
              </Grid>
            }
            {typeof window !== 'undefined' &&
              <Grid item className={classes.iconLink}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-printer-fill" viewBox="0 0 16 16">
                  <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z" />
                  <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
                </svg>
                <button className="print-button" type="button" href='#' onClick={handlePrint}>PRINT LISTING</button>
              </Grid>
            }
            <Grid item className={classes.iconLink}>
              <EmailShareButton
                url={currentURL}
                subject={`Look at this "${title}" property I found`}
                body="What do you think about this property?"
                onClick={() => { }}
                openShareDialogOnClick>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-envelope-fill" viewBox="0 0 16 16">
                  <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z" />
                </svg>&nbsp; EMAIL THIS PROPERTY
              </EmailShareButton>
              <br />
            </Grid>
            <Grid item xs={12}>
              <Button href="#contact-rentals" style={{ minWidth: 273 }} disableElevation variant="contained"
                size="medium" color="primary">Contact Us About {title}</Button>
            </Grid>
          </>
        }
        {(!ctaType || ctaType !== 'rental') &&
          <>
            <Grid item xs={12} className="listingCta">
              <Box align="center">
                <Typography variant="h1" gutterBottom>
                  <strong>{node.sfhouse?.address || node.sfhouse?.title || title}</strong>
                </Typography>
                {headline &&
                  <Typography variant="h4" gutterBottom>
                    <strong>{headline}</strong>
                  </Typography>
                }

                {homesite?.includes('Waterview') &&
                  <Box className="waterfront" py={1} px={2} color="white" bgColor="primary">Waterview</Box>
                }
                {!homesite?.includes('Waterview') && homesite?.includes('Waterfront') &&
                  <Box className="waterfront" py={1} px={2} color="white" bgColor="primary">Waterfront</Box>
                }

                {beds > 0 &&
                  <Typography component="span">{beds} &nbsp;
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.0475 7.08658H8.46507V12.8462H1.88266V5.44098H0.237061V17.783H1.88266V15.3146H16.6931V17.783H18.3387V10.3778C18.3387 9.5049 17.9919 8.66777 17.3747 8.05055C16.7575 7.43333 15.9204 7.08658 15.0475 7.08658ZM5.17386 12.0234C5.82853 12.0234 6.45637 11.7633 6.91929 11.3004C7.3822 10.8375 7.64227 10.2096 7.64227 9.55498C7.64227 8.90032 7.3822 8.27247 6.91929 7.80956C6.45637 7.34664 5.82853 7.08658 5.17386 7.08658C4.5192 7.08658 3.89136 7.34664 3.42844 7.80956C2.96553 8.27247 2.70546 8.90032 2.70546 9.55498C2.70546 10.2096 2.96553 10.8375 3.42844 11.3004C3.89136 11.7633 4.5192 12.0234 5.17386 12.0234Z" fill="inherit" />
                    </svg>&nbsp;BEDROOMS
                    &nbsp; &nbsp; &nbsp;</Typography>
                }

                {splitBaths == 1 && (bathFull > 0 || bathHalf > 0) &&
                  <Typography component="span">{bathFull}/{bathHalf} &nbsp;
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 512 512">
                      <path d="M32,384c0,28.3,12.5,53.5,32,71.1V496c0,8.8,7.2,16,16,16h32c8.8,0,16-7.2,16-16v-15.1h256V496c0,8.8,7.2,16,16,16h32c8.8,0,16-7.2,16-16v-40.9c19.5-17.6,32-42.8,32-71.1v-32H32V384z M496,256H96V77.2c0-10.8,15-17,22.6-9.4l13.8,13.8c-8.8,26.9-3,52.8,11.8,71.5c-6.3,6.3-6.4,16.6-0.2,22.8l11.3,11.3c6.2,6.2,16.4,6.2,22.6,0L283.3,81.9c6.2-6.2,6.2-16.4,0-22.6L272,48c-6.2-6.2-16.4-6.2-22.6,0c-18.7-14.7-44.7-20.4-71.7-11.6l-13.8-13.8C149.5,8.2,129.6,0,109.3,0C66.7,0,32,34.7,32,77.2V256l-16,0c-8.8,0-16,7.2-16,16v32c0,8.8,7.2,16,16,16h480c8.8,0,16-7.2,16-16v-32C512,263.2,504.8,256,496,256z" fill="inherit" />
                    </svg>&nbsp;BATHROOMS
                  </Typography>
                }
                {splitBaths == 0 && bathAll > 0 &&
                  <Typography component="span">{bathAll}&nbsp;
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 512 512">
                      <path d="M32,384c0,28.3,12.5,53.5,32,71.1V496c0,8.8,7.2,16,16,16h32c8.8,0,16-7.2,16-16v-15.1h256V496c0,8.8,7.2,16,16,16h32c8.8,0,16-7.2,16-16v-40.9c19.5-17.6,32-42.8,32-71.1v-32H32V384z M496,256H96V77.2c0-10.8,15-17,22.6-9.4l13.8,13.8c-8.8,26.9-3,52.8,11.8,71.5c-6.3,6.3-6.4,16.6-0.2,22.8l11.3,11.3c6.2,6.2,16.4,6.2,22.6,0L283.3,81.9c6.2-6.2,6.2-16.4,0-22.6L272,48c-6.2-6.2-16.4-6.2-22.6,0c-18.7-14.7-44.7-20.4-71.7-11.6l-13.8-13.8C149.5,8.2,129.6,0,109.3,0C66.7,0,32,34.7,32,77.2V256l-16,0c-8.8,0-16,7.2-16,16v32c0,8.8,7.2,16,16,16h480c8.8,0,16-7.2,16-16v-32C512,263.2,504.8,256,496,256z" fill="inherit" />
                    </svg>&nbsp;BATHROOMS
                  </Typography>
                }
                <br />
                {sqft > 0 &&
                  <Box my={2}>
                    <Typography component="span">SQUARE
                      FOOTAGE: {sqft.toLocaleString()} SQ. FT.</Typography>
                  </Box>
                }
                <Box my={3}>
                  <Typography
                    component="span"><strong>PRICE: {price ? ` $${price?.toLocaleString()}` : `CONTACT FOR PRICING`}</strong>
                    <br />
                    {leaseFee && <strong>LEASE FEE: {leaseFee ? ` $${leaseFee?.toLocaleString()}` : `CONTACT FOR PRICING`}
                    </strong>}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item className={classes.iconLink}>
              {typeof window !== 'undefined' &&
                <Grid item className={classes.iconLink}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-printer-fill" viewBox="0 0 16 16">
                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z" />
                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
                  </svg>
                  <button className="print-button" type="button" href='#' onClick={handlePrint}>PRINT LISTING</button>
                </Grid>
              }
              <EmailShareButton
                url={currentURL}
                subject={`Look at this "${title}" property I found`}
                body="What do you think about this property?"
                onClick={() => { }}
                openShareDialogOnClick>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-envelope-fill" viewBox="0 0 16 16">
                  <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555ZM0 4.697v7.104l5.803-3.558L0 4.697ZM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757Zm3.436-.586L16 11.801V4.697l-5.803 3.546Z" />
                </svg>&nbsp; EMAIL THIS LISTING</EmailShareButton>
              <br />
            </Grid>
            <Grid item xs={12}>
              <Button
                // href=
                onClick={handleOpen}
                style={{ minWidth: 273 }} disableElevation variant="contained"
                size="medium" color="primary">Contact Sales Team</Button>
            </Grid>
            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby="simple-modal-title"
              aria-describedby="simple-modal-description"
            >
              <div className={classes.modalWindow}>
                <a className={classes.modalClose} href="#" onClick={handleClose}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-x-lg" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                  </svg>
                </a>
                <iframe frameBorder="0" height={sitewideContext?.site == 'potnets' ? 720 : 590} width={sitewideContext?.site == 'potnets' ? 350 : 300} src={sitewideContext?.site == 'potnets' ? iFrameParameter : iFrameParameterBaywood} title={title || 'Form CTA'}></iframe>

              </div>
            </Modal>
            {houseFiles?.length > 0 &&
              houseFiles.map((v, i) => {
                return (
                  <Grid key={`house-files-${i}`} item>
                    <Button disableElevation href={v.src} variant="contained" size="medium">DOWNLOAD FLOORPLAN #{i + 1}</Button>
                  </Grid>
                );
              })
            }
          </>
        }
      </Grid>
    </Box>
  );
}

